import React, { Fragment } from 'react';

import { type BaseElement, Node } from 'slate';
import { RenderElementProps } from 'slate-react';

import type { RenderLeafType, Wrex } from '../types';
import { isRTL } from '../utils/isRTL';

const isElementRTL = (element: BaseElement): boolean => {
    try {
        const text = Node.string(element);
        return isRTL(text);
    } catch {
        return false;
    }
};

/**
 * We have to add a non editable invisible character at the beginning and
 * the end of each inline element to make sure that we can always navigate
 * out of the element. This is to fix a bug with Chromium. It doesn't have any
 * impact on other browsers.
 */
const InlineChromiumBugfix = () => (
    // eslint-disable-next-line react/forbid-elements
    <span contentEditable={false} style={{ fontSize: 0 }}>
        {String.fromCodePoint(160) /* Non-breaking space */}
    </span>
);

type RenderersIndex = Record<string, React.FC<any>>;

/**
 * Composes element renderers into one renderer.
 *
 * @param DefaultRenderer The default renderer if the type is not found in the index
 * @param renderers       Index of renderers by element type.
 */
export const renderElements =
    (
        DefaultRenderer: React.FC<any>,
        renderers: RenderersIndex = {},
    ): ((props: RenderElementProps & { isReadOnly?: boolean }) => React.JSX.Element) =>
    (props) => {
        const { attributes, children, element, isReadOnly } = props;
        const { ref, ...forwardedProps } = attributes;
        const Renderer = renderers[(element as Wrex.Element).type as string];
        const isElementInline = attributes['data-slate-inline'];

        // Detect RTL text and explicitly set dir="rtl" on block elements only.
        // Inline elements (links, mentions, etc.) inherit direction from their parent block.
        // This is used to correctly detect RTL mixed with some LTR on copy paste.
        // Note: slate is already doing something similar (https://github.com/ianstormtaylor/slate/blob/7b2bb0f96c6fa753fbbfe7b128366a6218cb264c/packages/slate-react/src/components/element.tsx#L104)
        // but they are relying on a library that checks only the first character of the text.
        if (!isElementInline) {
            if (isElementRTL(element)) {
                forwardedProps.dir = 'rtl';
            }
        }

        if (Renderer) {
            const isElementVoid = attributes['data-slate-void'];

            return (
                <Renderer elementRef={ref} {...forwardedProps} readOnly={isReadOnly} element={element}>
                    {isElementInline && !isElementVoid && <InlineChromiumBugfix />}
                    {children}
                    {isElementInline && !isElementVoid && <InlineChromiumBugfix />}
                </Renderer>
            );
        }

        return (
            <DefaultRenderer elementRef={ref} {...forwardedProps}>
                {children}
            </DefaultRenderer>
        );
    };

/**
 * Composes a base leaf renderer with mark renderers.
 *
 * @param markRenderers Index of renderers by mark type.
 * @param options.viewMode activate to avoid using span (use fragments).
 */
export const renderLeafs =
    (markRenderers: RenderersIndex = {}, options: { viewMode?: boolean } = {}): RenderLeafType =>
    (props) => {
        const { attributes, leaf, children } = props;

        const content = Object.entries(markRenderers)
            .filter(([leafType]) => (leaf as Wrex.Text)[leafType])
            .reduce(
                (acc, [, Renderer]) => (
                    <Renderer attributes={attributes} leaf={leaf}>
                        {acc}
                    </Renderer>
                ),
                children,
            );

        // Use <span> in edition mode to help slate match to an actual DOM node
        // Use Fragment in view mode to reduce the number of unnecessary DOM nodes.
        const Leaf = options?.viewMode ? Fragment : 'span';
        return <Leaf {...attributes}>{content}</Leaf>;
    };
